import React, {useCallback, useEffect, useRef, useState} from "react";
import {selectUserAccount} from "../../../../stores/account/account.selectors";
import {useSelector, useDispatch} from "react-redux";
import {updateAddressAsync} from "../../../../stores/account/account.thunks";
import GlobalConst from "../../../../constants/GlobalConst";
import tradefanApi, {useGetProductsQuery} from "../../../../stores/tradefan/tradefan.api";
import {
    resetSkrillTransactionStatus,
    setSkrillTransactionStatus,
    setSessionId
} from "../../../../stores/ecommerce/ecommerce.slice";
import {triggerEventAsync} from "../../../../stores/marketing/marketing.thunks";

export const ViewMode ={
    PRODUCT_SELECTION : 1,
    PAYMENT_REQUEST_FORM : 2,
    TRANSACTION_PROCESSING: 3,
    TRANSACTION_COMPLETED: 4,
    TRANSACTION_FAILED: 5
};

export const steps = [
    ViewMode.PRODUCT_SELECTION,
    ViewMode.PAYMENT_REQUEST_FORM,
];

const useSkrillViewModel = (props)=>{

    const dispatch = useDispatch();
    const [viewMode, setViewMode] = useState(ViewMode.PRODUCT_SELECTION);
    const submitting = useRef(false);
    const account = useSelector(selectUserAccount);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [billingAddress, setBillingAddress] = useState(account.address);
    const [saveAsHomeAddress, setSaveAsHomeAddress] = useState(true);
    const [paymentRequest, setPaymentRequest] = useState({
        FirstName: account.firstName,
        LastName: account.lastName,
        Email: account.email,
        Phone: account.address.phone1,
        Address: account.address.address1,
        City: account.address.city,
        State: account.address.state,
        PostalCode: account.address.zipCode,
        Country: account.address.countryCode,
        Currency: GlobalConst.SITE_CURRENCY,
        Amount: 0,
        Subject: "Add Funds", //todo: localize
        Note: "Add Funds",//todo: localize
        Details1Description: "Add Funds",//todo: localize
        Details1Text: "Add Funds",//todo: localize
    });
    const [editBillingAddress, setEditBillingAddress] = useState(true);
    const skrillTransactionStatus = useSelector(state => state.ecommerce.skrill.transactionStatus);
    const sessionId = useSelector(state => state.ecommerce.sessionId);
    const errors = useSelector(state => state.ecommerce.errors);
    const {data:products, error:productsError} = useGetProductsQuery();
    const firedRef = useRef(false);

    const [displayProducts, setDisplayProducts] = useState([]);

    useEffect(()=>{

        const hasProducts = !!products && products.length > 0;

        if(displayProducts.length === 0 && hasProducts) {
            const prods = products.filter(p=>p.name != "Other");
            const other = products.find(p=>p.name === "Other");
            setDisplayProducts([...prods, {...other, price: GlobalConst.MIN_DEPOSIT_FUNDS}]);
            //set default product — prefer entry fee from parlay context, fallback to global default
            const targetAmount = parseFloat(props.parlayContext?.entryFee ?? GlobalConst.DEFAULT_DEPOSIT_AMOUNT);
            const matchedProduct = prods.find(p => parseFloat(p.price) === targetAmount);
            let defaultProduct = null;
            if (matchedProduct) {
                defaultProduct = matchedProduct;
            } else if (other) {
                defaultProduct = {...other, price: targetAmount};
            } else if (prods.length > 0) {
                defaultProduct = prods.find(p => p.price === GlobalConst.DEFAULT_DEPOSIT_AMOUNT) || prods[0];
            }
            if (defaultProduct) {
                setSelectedProduct(defaultProduct);
                setPaymentRequest(prev => ({...prev, Amount: defaultProduct.price}));
            }
        }

        if (hasProducts && !firedRef.current) {
            firedRef.current = true;
            dispatch(triggerEventAsync({ eventName: 'addFundsStart', products: products }));
        }

    },[products, dispatch]);


    useEffect(()=>{
        submitting.current = false;
    },[]);

    useEffect(() => {
        if (skrillTransactionStatus === "Approved") {
            setViewMode(ViewMode.TRANSACTION_COMPLETED);
        } else if (skrillTransactionStatus === "Failed") {
            setViewMode(ViewMode.TRANSACTION_FAILED);
        }
    }, [skrillTransactionStatus]);

    const onProductSelected = (p)=>{
        setSelectedProduct(p);
        setPaymentRequest({
            ...paymentRequest,
            Amount: p.price,
        });
    }

    const handlePaymentRequestSubmitClick = useCallback(async (e)=>{
        if(submitting.current)
            return;
        submitting.current = true;

        if (saveAsHomeAddress)
            dispatch(updateAddressAsync({address: {
                    ...billingAddress,
                    addressId: account.addressId,
                },skipLastAccountUpdate: true}));

        let request = {
            ...paymentRequest,
            Amount: selectedProduct?.price ?? paymentRequest.Amount,
            Hostname: window.location.hostname === "localhost"
                ? "pickx.tradefan" + GlobalConst.SITE_TLD
                : window.location.hostname
        };
        if(editBillingAddress){
            request = {
                ...request,
                Address: billingAddress.address1,
                City: billingAddress.city,
                State: billingAddress.state,
                PostalCode: billingAddress.zipCode,
                Country: billingAddress.countryCode,
            }
        }

        try {
            const res = await dispatch(tradefanApi.endpoints.initiateSkrillPayment.initiate(request));
            if(res.error){
                dispatch(setSkrillTransactionStatus({
                    transactionStatus:'Failed',
                    error: res.error.data.Error
                }));
            }
            else if(res.data?.Success && res.data?.SessionId) {
                const sId = res.data.SessionId;
                setViewMode(ViewMode.TRANSACTION_PROCESSING);
                dispatch(setSessionId(sId));
                dispatch(setSkrillTransactionStatus({
                    transactionStatus:'Processing'
                }));
                openTransactionWindow(sId);
            }
        }catch(err){
            dispatch(setSkrillTransactionStatus({
                transactionStatus:'Failed',
                error: err.message
            }));
        }
        finally {
            submitting.current = false;
        }

    },[selectedProduct, billingAddress, saveAsHomeAddress, paymentRequest, editBillingAddress]);

    useEffect(()=>{
        if (skrillTransactionStatus === "APPROVED") {
            dispatch(setSkrillTransactionStatus({transactionStatus:'Processing'}));
        }
    },[skrillTransactionStatus])

    const handleReturnToProdSelectionClick = ()=>{
        setViewMode(ViewMode.PRODUCT_SELECTION);
        dispatch(resetSkrillTransactionStatus());
    }
    const handleOrderConfirmedOkClick = ()=>{
        dispatch(resetSkrillTransactionStatus());
        props.onClose();
        if(props.onCompleted)
            props.onCompleted();
    }
    const handleProductionSelectionSubmitClick = ()=>{
        setViewMode(ViewMode.PAYMENT_REQUEST_FORM);
    }
    const openTransactionWindow = (sId)=>{
        if(!sId)
            sId = sessionId;
        if(!sId)
            return;
        window.open('https://pay.skrill.com?sid='+sId, "_blank");
    }

    return {
        sessionId,
        submitting: submitting.current,
        errors,
        viewMode,
        steps,
        products : displayProducts,
        paymentRequest, setPaymentRequest,
        saveAsHomeAddress, setSaveAsHomeAddress,
        selectedProduct, setSelectedProduct,
        editBillingAddress, setEditBillingAddress,
        billingAddress, setBillingAddress,
        handleReturnToProdSelectionClick,
        handleOrderConfirmedOkClick,
        onProductSelected,
        handleProductionSelectionSubmitClick,
        handlePaymentRequestSubmitClick,
        openTransactionWindow
    };
};
export default useSkrillViewModel;